import { useCallback, useState } from 'react';
import axios from 'axios';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { X } from 'lucide-react';
import { useShowMore } from '../../components/useShowMore';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const TransactionsAdminTab = ({ loading, transactions, formatDate }) => {
    // Local filter — when the admin clicks an actor name in the Who column,
    // refetch the transaction list scoped to just that actor's activity.
    // Fetch is triggered directly from the click handler (not via an effect)
    // to keep the data flow synchronous with the user gesture.
    const [actorFilter, setActorFilter] = useState(null);
    const [playerFilter, setPlayerFilter] = useState(null);
    const [filteredRows, setFilteredRows] = useState(null);
    const [filterLoading, setFilterLoading] = useState(false);
    const [sinceDays, setSinceDays] = useState(null); // null = all-time

    const fetchFiltered = useCallback(async (actor, player, days) => {
        // Only hit the API when at least one filter is active — otherwise
        // fall back to the parent-fetched `transactions` prop.
        if (!actor && !player && !days) {
            setFilteredRows(null);
            return;
        }
        setFilteredRows(null);
        setFilterLoading(true);
        try {
            const params = { limit: 200 };
            if (actor) params.actor_id = actor.id;
            if (player) params.user_id = player.id;
            if (days) params.since_days = days;
            const { data } = await axios.get(`${API}/admin/transactions`, {
                params, withCredentials: true,
            });
            setFilteredRows(data || []);
        } catch {
            setFilteredRows([]);
        } finally {
            setFilterLoading(false);
        }
    }, []);

    const applyActorFilter = useCallback(async (actor) => {
        setActorFilter(actor);
        await fetchFiltered(actor, playerFilter, sinceDays);
    }, [fetchFiltered, playerFilter, sinceDays]);

    const applyPlayerFilter = useCallback(async (player) => {
        setPlayerFilter(player);
        await fetchFiltered(actorFilter, player, sinceDays);
    }, [fetchFiltered, actorFilter, sinceDays]);

    const setDateRange = useCallback(async (days) => {
        const newDays = sinceDays === days ? null : days;
        setSinceDays(newDays);
        await fetchFiltered(actorFilter, playerFilter, newDays);
    }, [fetchFiltered, actorFilter, playerFilter, sinceDays]);

    const clearFilter = useCallback(() => {
        setActorFilter(null);
        setPlayerFilter(null);
        setSinceDays(null);
        setFilteredRows(null);
    }, []);

    const rows = filteredRows ?? (transactions || []);
    const isLoading = loading || filterLoading;
    const hasAnyFilter = !!actorFilter || !!playerFilter || !!sinceDays;
    const { visibleItems, ShowMoreButton, total, limit } = useShowMore(rows, 10, 10);

    return (
        <div className="card-dark p-6">
            {/* Filter bar — date range + active actor chip */}
            <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="trans-filter-bar">
                <span className="text-[10px] uppercase tracking-wide text-[#666] font-bold">When:</span>
                {[
                    { d: null, label: 'All' },
                    { d: 1, label: 'Today' },
                    { d: 7, label: '7d' },
                    { d: 30, label: '30d' },
                ].map(opt => {
                    const active = sinceDays === opt.d;
                    return (
                        <button key={opt.label}
                            type="button"
                            onClick={() => setDateRange(opt.d)}
                            className={`px-2.5 py-1 rounded-md text-xs font-bold border transition ${
                                active
                                    ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                    : 'bg-[#0a0a0a] text-white border-white/10 hover:border-[#FFD700]/40'
                            }`}
                            data-testid={`trans-range-${opt.label.toLowerCase()}`}>
                            {opt.label}
                        </button>
                    );
                })}

                {actorFilter && (
                    <>
                        <span className="text-[10px] text-[#666] ml-3">·</span>
                        <span className="text-[10px] uppercase tracking-wide text-[#666] font-bold">Who:</span>
                        <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${actorRoleClass(actorFilter.role)}`}
                            data-testid="actor-filter-chip">
                            {actorFilter.name}
                        </span>
                    </>
                )}

                {playerFilter && (
                    <>
                        <span className="text-[10px] text-[#666] ml-3">·</span>
                        <span className="text-[10px] uppercase tracking-wide text-[#666] font-bold">Player:</span>
                        <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-[#FF007F]/15 text-[#FF77B5] border border-[#FF007F]/30"
                            data-testid="player-filter-chip">
                            {playerFilter.name}
                        </span>
                    </>
                )}

                {hasAnyFilter && (
                    <Button size="sm" variant="outline" onClick={clearFilter}
                        className="h-6 px-2 border-white/10 text-[#A0AAB2] hover:text-white ml-1"
                        data-testid="clear-trans-filter">
                        <X className="w-3 h-3 mr-1" /> Clear
                    </Button>
                )}

                {hasAnyFilter && (
                    <span className="text-[10px] text-[#666] ml-auto">
                        {rows.length} match{rows.length === 1 ? '' : 'es'}
                    </span>
                )}
            </div>

            {isLoading ? (
                <div className="flex justify-center py-12"><div className="spinner" /></div>
            ) : rows.length === 0 ? (
                <p className="text-center text-[#A0AAB2] py-12">
                    {hasAnyFilter
                        ? `No transactions match the current filter${actorFilter ? ` for ${actorFilter.name}` : ''}.`
                        : 'No transactions yet'}
                </p>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-white/10">
                                    <TableHead className="text-[#A0AAB2]">Type</TableHead>
                                    <TableHead className="text-[#A0AAB2]">Player</TableHead>
                                    <TableHead className="text-[#A0AAB2]">Description</TableHead>
                                    <TableHead className="text-[#A0AAB2]">Who</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Amount</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Balance</TableHead>
                                    <TableHead className="text-[#A0AAB2]">Date</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {visibleItems.map((tx, i) => {
                                    const ttype = tx.transaction_type || tx.type || '';
                                    const canFilter = !!tx.actor_id;
                                    return (
                                        <TableRow key={i} className="border-white/10">
                                            <TableCell>
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                    ttype.includes('deposit') ? 'bg-[#39FF14]/20 text-[#39FF14]'
                                                    : ttype.includes('withdraw') || ttype.includes('cashout') ? 'bg-[#FF3B30]/20 text-[#FF3B30]'
                                                    : ttype === 'game' ? 'bg-[#00FFFF]/20 text-[#00FFFF]'
                                                    : 'bg-[#FFD700]/20 text-[#FFD700]'
                                                }`}>{ttype || 'unknown'}</span>
                                            </TableCell>
                                            <TableCell data-testid={`tx-player-${i}`}>
                                                {tx.player_name && tx.user_id ? (
                                                    <button type="button"
                                                        onClick={() => applyPlayerFilter({ id: tx.user_id, name: tx.player_name })}
                                                        className="text-xs font-bold text-[#FF77B5] underline-offset-2 hover:underline cursor-pointer"
                                                        title={`Filter to ${tx.player_name}'s transactions only`}
                                                        data-testid={`tx-player-filter-${i}`}>
                                                        {tx.player_name}
                                                    </button>
                                                ) : (
                                                    <span className="text-[10px] text-[#666] italic">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-white max-w-xs truncate">{tx.description}</TableCell>
                                            <TableCell data-testid={`tx-actor-${i}`}>
                                                {tx.actor_name ? (
                                                    canFilter ? (
                                                        <button type="button"
                                                            onClick={() => applyActorFilter({ id: tx.actor_id, name: tx.actor_name, role: tx.actor_role })}
                                                            className={`text-xs font-semibold underline-offset-2 hover:underline cursor-pointer ${actorRoleClass(tx.actor_role)}`}
                                                            title={`Filter Trans list to only ${tx.actor_name}'s activity`}
                                                            data-testid={`tx-actor-filter-${i}`}>
                                                            {tx.actor_name}
                                                        </button>
                                                    ) : (
                                                        <span className={`text-xs font-semibold ${actorRoleClass(tx.actor_role)}`}>
                                                            {tx.actor_name}
                                                        </span>
                                                    )
                                                ) : (
                                                    <span className="text-[10px] text-[#666] italic">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell className={`text-right font-mono ${tx.amount >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                {tx.amount >= 0 ? '+' : ''}{(tx.amount || 0).toFixed(2)}
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-[#A0AAB2]">{(tx.balance_after || 0).toFixed(2)}</TableCell>
                                            <TableCell className="text-[#A0AAB2]">{formatDate(tx.created_at)}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                    <p className="text-[10px] text-[#A0AAB2] text-center mt-2">
                        Showing {Math.min(limit, total)} of {total}
                    </p>
                    <ShowMoreButton testid="transactions-show-more" />
                </>
            )}
        </div>
    );
};

const actorRoleClass = (role) => (
    role === 'admin' ? 'text-[#FFD700]'
    : role === 'operator' ? 'text-[#00FFFF]'
    : role === 'cashier' ? 'text-[#39FF14]'
    : role === 'player' ? 'text-[#A0AAB2]'
    : role === 'system' ? 'text-[#666] italic'
    : 'text-white'
);
